import { api } from 'aws-blocks';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import BkButton from '../../Components/ui/BkButton';
import BkPage from '../../Components/ui/BkPage';
import BkPanel from '../../Components/ui/BkPanel';
import Coin from '../../Components/Coin';
import Gem from '../../Components/Gem';
import EmoteIcon from '../../Components/EmoteIcon';
import ProfileAvatar from '../../Components/ProfileAvatar';
import AgeConfirm from '../../Components/AgeConfirm';
import { useAuthContext } from '../../contexts/AuthContext';
import { useSnackBarContext } from '../../contexts/SnackBarContext';
import usePageMeta from '../../hooks/use-page-meta';
import { formatCurrencyAmount } from '../../utils/format-currency';

type PassGrant = {
  type: string;
  amount?: number;
  id?: string;
};

type PassTier = {
  tier: number;
  xpRequired: number;
  free?: PassGrant;
  premium?: PassGrant;
};

type BattlePassPayload = {
  season: {
    seasonId: string;
    name: string;
    startsAt: number;
    endsAt: number;
    active: boolean;
    claimGrace?: boolean;
    priceGems: number;
    priceMoney: number;
    xpPerTier: number;
    tierCount: number;
    tiers: PassTier[];
    endedSeasons?: Array<{ seasonId: string; name: string; endsAt: number }>;
  };
  progress: {
    seasonId: string;
    premium: boolean;
    xp: number;
    claimedFree: number[];
    claimedPremium: number[];
  };
  currentTier: number;
  missions: Array<{
    id: string;
    label: string;
    passXp: number;
    complete: boolean;
    claimed: boolean;
  }>;
};

function grantLabel(grant?: PassGrant): string {
  if (!grant) return '—';
  switch (grant.type) {
    case 'gems':
      return `${grant.amount} gems`;
    case 'coins':
      return `${grant.amount} coins`;
    case 'xpBoostDays':
      return `${grant.amount}d XP boost`;
    case 'coinBoostDays':
      return `${grant.amount}d coin boost`;
    case 'icon':
      return 'Portrait';
    case 'emote':
      return 'Emote';
    case 'skin':
      return 'Skin';
    default:
      return grant.type;
  }
}

function GrantPreview({ grant }: { grant?: PassGrant }) {
  if (!grant) {
    return <span className="text-bk-muted">—</span>;
  }
  if (grant.type === 'icon' && grant.id) {
    return <ProfileAvatar avatarId={grant.id} size="sm" ring={false} />;
  }
  if (grant.type === 'emote' && grant.id) {
    return <EmoteIcon emoteId={grant.id} size="sm" />;
  }
  if (grant.type === 'gems') {
    return (
      <span className="inline-flex items-center gap-1 text-fuchsia-300">
        <Gem />
        {grant.amount}
      </span>
    );
  }
  if (grant.type === 'coins') {
    return (
      <span className="inline-flex items-center gap-1 text-bk-gold">
        <Coin />
        {grant.amount}
      </span>
    );
  }
  return (
    <span className="text-xs font-semibold uppercase tracking-wide text-bk-text">
      {grantLabel(grant)}
    </span>
  );
}

export default function Pass() {
  usePageMeta({
    title: 'Battle Pass',
    description: 'Season 1 Crownfall — climb the free and premium tracks.',
    path: '/pass',
  });

  const { currentUser, openLoginModal, queryUserData, userData } =
    useAuthContext();
  const { addSnackBar } = useSnackBarContext();
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState<BattlePassPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [confirm18, setConfirm18] = useState(false);

  const load = useCallback(async () => {
    if (!currentUser) {
      setData(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const result = (await api.getBattlePass()) as BattlePassPayload;
      setData(result);
    } catch (err) {
      addSnackBar({
        title: 'ERROR',
        message: err instanceof Error ? err.message : String(err),
        variant: 'danger',
        timeout: 3000,
      });
    } finally {
      setLoading(false);
    }
  }, [currentUser, addSnackBar]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    const checkout = searchParams.get('checkout');
    const sessionId = searchParams.get('session_id');
    if (!checkout) return;

    const clearParams = () => {
      const next = new URLSearchParams(searchParams);
      next.delete('checkout');
      next.delete('session_id');
      setSearchParams(next, { replace: true });
    };

    if (checkout === 'cancel') {
      addSnackBar({
        title: 'Checkout canceled',
        message: 'No charge was made.',
        variant: 'info',
        timeout: 2500,
      });
      clearParams();
      return;
    }

    if (checkout === 'success' && sessionId && currentUser) {
      void (async () => {
        try {
          await api.confirmBattlePassCheckout(sessionId);
          await queryUserData();
          await load();
          addSnackBar({
            title: 'Premium unlocked',
            message: 'Season 1 premium track is yours.',
            variant: 'success',
            timeout: 3500,
          });
        } catch (err) {
          addSnackBar({
            title: 'ERROR',
            message:
              err instanceof Error
                ? err.message
                : 'Could not confirm battle pass purchase.',
            variant: 'danger',
            timeout: 4000,
          });
        } finally {
          clearParams();
        }
      })();
    }
  }, [
    searchParams,
    setSearchParams,
    currentUser,
    queryUserData,
    addSnackBar,
    load,
  ]);

  const progressPct = useMemo(() => {
    if (!data) return 0;
    const { xp } = data.progress;
    const next = data.season.tiers.find((t) => t.xpRequired > xp);
    const prevXp =
      data.currentTier > 0
        ? data.season.tiers[data.currentTier - 1]?.xpRequired ?? 0
        : 0;
    const nextXp = next?.xpRequired ?? data.season.tiers.at(-1)?.xpRequired ?? 1;
    if (nextXp <= prevXp) return 100;
    return Math.min(100, ((xp - prevXp) / (nextXp - prevXp)) * 100);
  }, [data]);

  async function handleClaim() {
    setBusy(true);
    try {
      const result = (await api.claimBattlePassTiers()) as {
        claimed?: unknown[];
      };
      await queryUserData();
      await load();
      const n = Array.isArray(result.claimed) ? result.claimed.length : 0;
      addSnackBar({
        title: n ? 'Rewards claimed' : 'All caught up',
        message: n
          ? `Claimed ${n} reward${n === 1 ? '' : 's'}.`
          : 'No new tiers to claim.',
        variant: 'success',
        timeout: 2500,
      });
    } catch (err) {
      addSnackBar({
        title: 'ERROR',
        message: err instanceof Error ? err.message : String(err),
        variant: 'danger',
        timeout: 3000,
      });
    } finally {
      setBusy(false);
    }
  }

  async function handleMissions() {
    setBusy(true);
    try {
      const result = (await api.claimBattlePassMissions()) as {
        gained?: number;
      };
      await queryUserData();
      await load();
      const gained = Number(result.gained ?? 0);
      addSnackBar({
        title: gained ? 'Missions claimed' : 'Nothing to claim',
        message: gained
          ? `+${gained} battle pass XP`
          : 'Complete weekly missions first.',
        variant: gained ? 'success' : 'info',
        timeout: 2500,
      });
    } catch (err) {
      addSnackBar({
        title: 'ERROR',
        message: err instanceof Error ? err.message : String(err),
        variant: 'danger',
        timeout: 3000,
      });
    } finally {
      setBusy(false);
    }
  }

  async function buyPremium(method: 'gems' | 'money') {
    if (method === 'money' && !confirm18) {
      addSnackBar({
        title: 'Age required',
        message: 'Confirm you are 18 or older before paying with a card.',
        variant: 'warning',
        timeout: 2500,
      });
      return;
    }
    setBusy(true);
    try {
      if (method === 'money') {
        const result = (await api.buyBattlePassPremium('money')) as {
          url?: string;
        };
        if (result.url) {
          window.location.assign(result.url);
          return;
        }
        throw new Error('No checkout URL');
      }
      await api.buyBattlePassPremium('gems');
      await queryUserData();
      await load();
      addSnackBar({
        title: 'Premium unlocked',
        message: 'Premium track is active. Claim your rewards.',
        variant: 'success',
        timeout: 3000,
      });
    } catch (err) {
      addSnackBar({
        title: 'ERROR',
        message: err instanceof Error ? err.message : String(err),
        variant: 'danger',
        timeout: 3500,
      });
    } finally {
      setBusy(false);
    }
  }

  if (!currentUser) {
    return (
      <BkPage narrow>
        <BkPanel className="space-y-4 text-center">
          <h1 className="text-2xl font-bold uppercase tracking-wider text-bk-gold">
            Battle Pass
          </h1>
          <p className="text-sm text-bk-muted">
            Sign in to track Season 1 progress and claim rewards.
          </p>
          <BkButton variant="primary" onClick={openLoginModal}>
            Sign in
          </BkButton>
        </BkPanel>
      </BkPage>
    );
  }

  if (loading || !data) {
    return (
      <BkPage>
        <p className="text-bk-muted animate-bk-pulse-soft">Loading battle pass…</p>
      </BkPage>
    );
  }

  const { season, progress, currentTier, missions } = data;
  const claimedFree = new Set(progress.claimedFree);
  const claimedPremium = new Set(progress.claimedPremium);
  const gems = Number(userData?.gems ?? 0);
  const canClaim = Boolean(season.active || season.claimGrace);
  const canBuy = Boolean(season.active);
  const ended = season.endedSeasons ?? [];

  return (
    <BkPage>
      <div className="mb-8 space-y-4">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div className="space-y-2">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-bk-gold">
              Battle Pass
            </p>
            <h1 className="text-2xl font-extrabold uppercase tracking-wider text-bk-text md:text-3xl">
              {season.name}
            </h1>
            <p className="max-w-xl text-sm text-bk-muted">
              Earn pass XP from rewarded matches and weekly missions. Free track
              drips gems and portraits; premium unlocks exclusive emotes, boosts,
              and ~430 gems back.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <BkButton
              variant="secondary"
              disabled={busy || !canClaim}
              onClick={() => void handleClaim()}
            >
              Claim tiers
            </BkButton>
            {!progress.premium ? (
              <>
                <BkButton
                  variant="primary"
                  disabled={busy || !canBuy || gems < season.priceGems}
                  onClick={() => void buyPremium('gems')}
                >
                  Buy · {formatCurrencyAmount(season.priceGems)} gems
                </BkButton>
                <BkButton
                  variant="ghost"
                  disabled={busy || !canBuy || !confirm18}
                  onClick={() => void buyPremium('money')}
                >
                  Buy · ${season.priceMoney.toFixed(2)}
                </BkButton>
              </>
            ) : (
              <span className="inline-flex items-center border border-bk-gold bg-bk-gold/15 px-3 py-1.5 text-xs font-bold uppercase tracking-wide text-bk-gold">
                Premium owned
              </span>
            )}
          </div>
          {!progress.premium && (
            <div className="w-full space-y-2">
              <AgeConfirm
                id="pass-age-18"
                checked={confirm18}
                onChange={setConfirm18}
              >
                I confirm I am 18 or older and I am buying the premium track for
                myself.
              </AgeConfirm>
            <p className="text-xs text-bk-muted">
              Purchases are subject to our{' '}
              <Link
                className="text-bk-gold underline-offset-2 hover:underline"
                to="/terms#purchases"
              >
                Terms of Service
              </Link>
              .
            </p>
            </div>
          )}
        </div>

        <BkPanel className="space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
            <span className="font-bold text-bk-gold">
              Tier {currentTier} / {season.tierCount}
            </span>
            <span className="tabular-nums text-bk-muted">
              {formatCurrencyAmount(progress.xp)} pass XP
            </span>
          </div>
          <div className="h-2.5 w-full overflow-hidden border border-bk-gold-dim/50 bg-bk-bg">
            <div
              className="h-full bg-gradient-to-r from-bk-gold-dim to-bk-gold-bright transition-[width]"
              style={{ width: `${progressPct}%` }}
            />
          </div>
          <p className="text-xs text-bk-muted">
            {season.active
              ? `Season ends ${new Date(season.endsAt).toLocaleDateString()}`
              : season.claimGrace
                ? `Claim grace through ${new Date(season.endsAt + 7 * 24 * 60 * 60 * 1000).toLocaleDateString()} — no new XP`
                : 'No active season'}
          </p>
        </BkPanel>

        {ended.length ? (
          <BkPanel className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="space-y-1">
              <h2 className="text-sm font-bold uppercase tracking-wider text-bk-gold">
                Legacy cosmetics
              </h2>
              <p className="text-sm text-bk-muted">
                Past season exclusives are for sale in the store after the season
                ends.
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              {ended.map((s) => (
                <Link
                  key={s.seasonId}
                  to={`/store/icons?legacy=${encodeURIComponent(s.seasonId)}`}
                >
                  <BkButton variant="secondary" size="sm">
                    Browse {s.name.replace(/^Season \d+ — /, '')} icons
                  </BkButton>
                </Link>
              ))}
              {ended.map((s) => (
                <Link
                  key={`${s.seasonId}-emotes`}
                  to={`/store/emotes?legacy=${encodeURIComponent(s.seasonId)}`}
                >
                  <BkButton variant="ghost" size="sm">
                    Emotes
                  </BkButton>
                </Link>
              ))}
            </div>
          </BkPanel>
        ) : null}
      </div>

      <div className="mb-10 grid gap-6 lg:grid-cols-[1fr_20rem]">
        <section className="space-y-2">
          <div className="grid grid-cols-[3rem_1fr_1fr] gap-2 border-b border-bk-gold-dim/40 pb-2 text-[11px] font-bold uppercase tracking-wider text-bk-muted">
            <span>Tier</span>
            <span>Free</span>
            <span className={progress.premium ? 'text-bk-gold' : ''}>
              Premium
            </span>
          </div>
          <div className="max-h-[70vh] space-y-1 overflow-y-auto pr-1">
            {season.tiers.map((tier) => {
              const unlocked = currentTier >= tier.tier;
              const freeClaimed = claimedFree.has(tier.tier);
              const premClaimed = claimedPremium.has(tier.tier);
              return (
                <div
                  key={tier.tier}
                  className={[
                    'grid grid-cols-[3rem_1fr_1fr] items-center gap-2 border px-2 py-2',
                    unlocked
                      ? 'border-bk-gold-dim/50 bg-bk-panel/80'
                      : 'border-bk-border bg-bk-panel/40 opacity-70',
                  ].join(' ')}
                >
                  <span className="text-sm font-extrabold tabular-nums text-bk-gold">
                    {tier.tier}
                  </span>
                  <div className="flex min-h-10 items-center gap-2">
                    <GrantPreview grant={tier.free} />
                    {tier.free ? (
                      <span className="text-[10px] uppercase text-bk-muted">
                        {freeClaimed
                          ? 'Claimed'
                          : unlocked
                            ? 'Ready'
                            : 'Locked'}
                      </span>
                    ) : null}
                  </div>
                  <div
                    className={[
                      'flex min-h-10 items-center gap-2',
                      progress.premium ? '' : 'opacity-50',
                    ].join(' ')}
                  >
                    <GrantPreview grant={tier.premium} />
                    {tier.premium ? (
                      <span className="text-[10px] uppercase text-bk-muted">
                        {!progress.premium
                          ? 'Premium'
                          : premClaimed
                            ? 'Claimed'
                            : unlocked
                              ? 'Ready'
                              : 'Locked'}
                      </span>
                    ) : null}
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        <aside className="space-y-4">
          <BkPanel className="space-y-3">
            <h2 className="text-sm font-bold uppercase tracking-wider text-bk-gold">
              Weekly missions
            </h2>
            <ul className="space-y-2">
              {missions.map((m) => (
                <li
                  key={m.id}
                  className="border border-bk-border bg-bk-bg/50 px-3 py-2 text-sm"
                >
                  <div className="font-semibold text-bk-text">{m.label}</div>
                  <div className="mt-1 flex justify-between text-[11px] uppercase tracking-wide text-bk-muted">
                    <span>+{m.passXp} XP</span>
                    <span>
                      {m.claimed
                        ? 'Claimed'
                        : m.complete
                          ? 'Ready'
                          : 'In progress'}
                    </span>
                  </div>
                </li>
              ))}
            </ul>
            <BkButton
              variant="secondary"
              size="sm"
              disabled={busy || !season.active}
              onClick={() => void handleMissions()}
            >
              Claim mission XP
            </BkButton>
          </BkPanel>
        </aside>
      </div>
    </BkPage>
  );
}
